/* eslint-disable indent */
/* eslint-disable function-paren-newline */
/* eslint-disable implicit-arrow-linebreak */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  TextField,
  Modal,
  ModalFooter,
  Selection,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { getNextVolumeNumber, volumeExists } from '../utils/volumeUtils';
import BarcodeGenerationModal from '../../ItemsList/modals/BarcodeGenerationModal';
import SequentialBarcodeSection from '../../ItemsList/components/SequentialBarcodeSection';

// Enhanced AddVolumeModal with integrated sequential barcode generation
const AddVolumeModal = ({
  open,
  onClose,
  onAdd,
  existingVolumes = [],
  fields,
  // Props for barcode generation
  enableAutoGenerator = false,
  autoGenerate = false,
  generateBarcodeValue,
  locationId,
  locationCode,
  existingBarcodes = [],
}) => {
  const intl = useIntl();
  const [volumePrefix, setVolumePrefix] = useState('مج.');
  const [volumeNumber, setVolumeNumber] = useState('');
  const [itemCount, setItemCount] = useState(1);
  const [customPrefix, setCustomPrefix] = useState('');
  const [isCustomPrefix, setIsCustomPrefix] = useState(false);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [batchCount, setBatchCount] = useState(1);

  // Barcode generation states
  const [showAdvancedBarcodeModal, setShowAdvancedBarcodeModal] =
    useState(false);
  const [barcodeSettings, setBarcodeSettings] = useState({
    enabled: true,
    startingBarcode: '',
    barcodes: [],
    isValid: true,
  });

  // Predefined prefixes
  const prefixOptions = [
    { value: 'مج.', label: 'مج.' },
    { value: 'VOL.', label: 'VOL.' },
    { value: 'custom', label: 'Custom' },
  ];

  // Initialize with next expected volume number
  useEffect(() => {
    if (open) {
      const nextNumber = getNextVolumeNumber(existingVolumes);
      setVolumeNumber(nextNumber.toString());
    }
  }, [open, existingVolumes]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setVolumePrefix('مج.');
      setVolumeNumber('');
      setItemCount(1);
      setCustomPrefix('');
      setIsCustomPrefix(false);
      setIsBatchMode(false);
      setBatchCount(1);
      setBarcodeSettings({
        enabled: true,
        startingBarcode: '',
        barcodes: [],
        isValid: true,
      });
    }
  }, [open]);

  const handlePrefixChange = (value) => {
    if (value === 'custom') {
      setIsCustomPrefix(true);
      setVolumePrefix('');
    } else {
      setIsCustomPrefix(false);
      setVolumePrefix(value);
      setCustomPrefix('');
    }
  };

  const handleCustomPrefixChange = (e) => {
    const value = e.target.value;
    setCustomPrefix(value);
    setVolumePrefix(value);
  };

  // Handle barcode settings change from SequentialBarcodeSection
  const handleBarcodeSettingsChange = (settings) => {
    setBarcodeSettings(settings);
  };

  const getFullVolumeString = (volumeNum = volumeNumber) => {
    const prefix = isCustomPrefix ? customPrefix : volumePrefix;
    return volumeNum ? `${prefix}${volumeNum}` : '';
  };

  const getVolumesToCreate = () => {
    if (!isBatchMode) {
      return [
        {
          volume: getFullVolumeString(),
          count: Math.max(1, parseInt(itemCount, 10) || 1),
        },
      ];
    }

    const volumes = [];
    const startNumber = parseInt(volumeNumber, 10);
    const count = parseInt(batchCount, 10);

    for (let i = 0; i < count; i++) {
      const volumeNum = startNumber + i;
      const volumeString = getFullVolumeString(volumeNum.toString());
      volumes.push({
        volume: volumeString,
        count: Math.max(1, parseInt(itemCount, 10) || 1),
      });
    }

    return volumes;
  };

  // Calculate total items to be created
  const getTotalItemsCount = () => {
    const volumesToCreate = getVolumesToCreate();
    return volumesToCreate.reduce((total, vol) => total + vol.count, 0);
  };

  // Handle advanced barcode modal confirmation
  const handleAdvancedBarcodeConfirm = (settings) => {
    setShowAdvancedBarcodeModal(false);
    // Proceed with volume creation using advanced settings
    proceedWithVolumeCreation(getVolumesToCreate(), settings);
  };

  // Proceed with volume creation
  const proceedWithVolumeCreation = (
    volumesToCreate,
    advancedSettings = null,
  ) => {
    let finalBarcodes = [];

    if (advancedSettings) {
      // Use advanced settings
      finalBarcodes = advancedSettings.barcodes || [];
    } else if (barcodeSettings.enabled && barcodeSettings.barcodes.length > 0) {
      // Use sequential generation from SequentialBarcodeSection
      finalBarcodes = barcodeSettings.barcodes;
    } else {
      // Use automatic generation or empty barcodes
      const totalItems = getTotalItemsCount();
      finalBarcodes = Array(totalItems)
        .fill('')
        .map((_, index) => {
          if (enableAutoGenerator && autoGenerate && generateBarcodeValue) {
            return generateBarcodeValue(locationId, locationCode, index + 1);
          }
          return '';
        });
    }

    // Create volumes with barcodes
    const volumesWithBarcodes = volumesToCreate.map(
      (volumeData, volumeIndex) => {
        const itemsWithBarcodes = [];
        let barcodeIndex = 0;

        // Calculate starting barcode index for this volume
        for (let i = 0; i < volumeIndex; i++) {
          barcodeIndex += volumesToCreate[i].count;
        }

        // Create items for this volume with appropriate barcodes
        for (let i = 0; i < volumeData.count; i++) {
          const currentBarcodeIndex = barcodeIndex + i;
          const barcode = finalBarcodes[currentBarcodeIndex] || '';

          const newItem = {
            ...volumeData,
            barcode,
            itemIndex: i,
            permanentLocation: { id: locationId }, // Add permanent location from holding
          };
          itemsWithBarcodes.push(newItem);
        }

        return {
          ...volumeData,
          items: itemsWithBarcodes,
        };
      },
    );

    // Call the original onAdd with enhanced data
    onAdd(volumesWithBarcodes, {
      mode: barcodeSettings.enabled ? 'sequential' : 'auto',
      startingBarcode: barcodeSettings.enabled
        ? barcodeSettings.startingBarcode
        : null,
      barcodes: finalBarcodes,
    });
    onClose();
  };

  // Enhanced handleAdd
  const handleAdd = () => {
    const volumesToCreate = getVolumesToCreate();
    const hasExistingVolumes = volumesToCreate.some((v) =>
      existingVolumes.includes(v.volume),
    );

    if (hasExistingVolumes) return;

    // Check validation for sequential barcode
    if (barcodeSettings.enabled && !barcodeSettings.isValid) {
      return;
    }

    proceedWithVolumeCreation(volumesToCreate);
  };

  const volumesToCreate = getVolumesToCreate();
  const hasExistingVolumes = volumesToCreate.some((v) =>
    volumeExists(v.volume, existingVolumes),
  );
  const isValidVolumeNumber =
    volumeNumber &&
    !Number.isNaN(parseInt(volumeNumber, 10)) &&
    parseInt(volumeNumber, 10) > 0;
  const isValidPrefix = isCustomPrefix
    ? customPrefix.trim() !== ''
    : volumePrefix !== '';
  const isValidBatchCount =
    !isBatchMode ||
    (batchCount &&
      !Number.isNaN(parseInt(batchCount, 10)) &&
      parseInt(batchCount, 10) > 0 &&
      parseInt(batchCount, 10) <= 100);

  const totalItemsCount = getTotalItemsCount();

  // Check if form is valid for submission
  const isFormValid = () => {
    const basicValidation =
      isValidVolumeNumber &&
      isValidPrefix &&
      isValidBatchCount &&
      itemCount > 0 &&
      !hasExistingVolumes;

    // If sequential barcode is enabled, validate barcode settings
    if (barcodeSettings.enabled) {
      return basicValidation && barcodeSettings.isValid;
    }

    return basicValidation;
  };

  // Render footer for fixed footer
  const renderFooter = (
    <ModalFooter>
      <Button
        buttonStyle="primary"
        onClick={handleAdd}
        disabled={!isFormValid()}
        marginBottom0
      >
        <FormattedMessage
          id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.add"
          defaultMessage="Add"
        />
      </Button>
      <Button onClick={onClose} marginBottom0>
        <FormattedMessage
          id="ui-plugin-inventory-templates.volumeManagement.modal.cancel"
          defaultMessage="Cancel"
        />
      </Button>
    </ModalFooter>
  );

  return (
    <>
      <Modal
        open={open}
        footer={renderFooter}
        label={
          <FormattedMessage
            id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.label"
            defaultMessage="Add New Volume"
          />
        }
        onClose={onClose}
        size="medium"
      >
        <div style={{ padding: '20px' }}>
          {/* Batch Mode Toggle */}
          <div style={{ marginBottom: '16px' }}>
            <Checkbox
              label={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.batchMode.label"
                  defaultMessage="Create multiple volumes"
                />
              }
              checked={isBatchMode}
              onChange={(e) => setIsBatchMode(e.target.checked)}
            />
          </div>

          {/* Volume Prefix Selection */}
          <div style={{ marginBottom: '16px' }}>
            <Selection
              label={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.volumePrefix.label"
                  defaultMessage="Volume Prefix"
                />
              }
              placeholder={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.volumeManagement.addVolumeModal.volumePrefix.placeholder',
                defaultMessage: 'Select prefix',
              })}
              dataOptions={prefixOptions}
              value={isCustomPrefix ? 'custom' : volumePrefix}
              onChange={handlePrefixChange}
            />
          </div>

          {/* Custom Prefix Input */}
          {isCustomPrefix && (
            <div style={{ marginBottom: '16px' }}>
              <TextField
                label={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.customPrefix.label"
                    defaultMessage="Custom Prefix"
                  />
                }
                value={customPrefix}
                onChange={handleCustomPrefixChange}
                placeholder={intl.formatMessage({
                  id: 'ui-plugin-inventory-templates.volumeManagement.addVolumeModal.customPrefix.placeholder',
                  defaultMessage: 'Enter custom prefix',
                })}
                required
              />
            </div>
          )}

          {/* Volume Number Input */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              label={
                isBatchMode ? (
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.startVolumeNumber.label"
                    defaultMessage="Starting Volume Number"
                  />
                ) : (
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.volumeNumber.label"
                    defaultMessage="Volume Number"
                  />
                )
              }
              type="number"
              min="1"
              value={volumeNumber}
              onChange={(e) => setVolumeNumber(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.volumeManagement.addVolumeModal.volumeNumber.placeholder',
                defaultMessage: 'Enter volume number',
              })}
              error={
                hasExistingVolumes ? (
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.volumeExists.error"
                    defaultMessage="One or more volumes already exist"
                  />
                ) : (
                  ''
                )
              }
              required
            />
          </div>

          {/* Batch Count Input */}
          {isBatchMode && (
            <div style={{ marginBottom: '16px' }}>
              <TextField
                label={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.batchCount.label"
                    defaultMessage="Number of Volumes"
                  />
                }
                type="number"
                min="1"
                max="100"
                value={batchCount}
                onChange={(e) => setBatchCount(e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'ui-plugin-inventory-templates.volumeManagement.addVolumeModal.batchCount.placeholder',
                  defaultMessage: 'Enter number of volumes',
                })}
                required
              />
            </div>
          )}

          {/* Item Count Input */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              label={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.itemCount.label"
                  defaultMessage="Number of Items per Volume"
                />
              }
              type="number"
              min="1"
              max="100"
              value={itemCount}
              onChange={(e) => setItemCount(e.target.value)}
              required
            />
          </div>

          {/* Volume Preview */}
          {getFullVolumeString() && (
            <div
              style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  color: '#6c757d',
                  marginBottom: '4px',
                }}
              >
                <FormattedMessage
                  id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.preview.label"
                  defaultMessage="Volume Preview:"
                />
              </div>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#495057',
                }}
              >
                {isBatchMode ? (
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.batchPreview"
                    values={{
                      startVolume: getFullVolumeString(),
                      endVolume: getFullVolumeString(
                        (
                          parseInt(volumeNumber, 10) +
                          parseInt(batchCount, 10) -
                          1
                        ).toString(),
                      ),
                      count: batchCount,
                    }}
                    defaultMessage="{startVolume} to {endVolume} ({count} volumes)"
                  />
                ) : (
                  getFullVolumeString()
                )}
              </div>
            </div>
          )}

          {/* Sequential Barcode Section Component */}
          {totalItemsCount >= 1 && (
            <SequentialBarcodeSection
              itemCount={totalItemsCount}
              onBarcodeSettingsChange={handleBarcodeSettingsChange}
              enableAutoGenerator={enableAutoGenerator}
              autoGenerate={autoGenerate}
              existingBarcodes={existingBarcodes}
              onAdvancedClick={() => setShowAdvancedBarcodeModal(true)}
              checkboxLabel={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.sequentialBarcode.useSequential.label',
                defaultMessage: 'Generate sequential barcodes',
              })}
              placeholder={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.sequentialBarcode.startingBarcode.placeholder',
                defaultMessage: 'e.g., BOOK2024001',
              })}
            />
          )}

          {/* Summary Info */}
          {getFullVolumeString() &&
            isValidVolumeNumber &&
            isValidBatchCount && (
              <div
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#e8f5e8',
                  border: '1px solid #c3e6c3',
                  borderRadius: '4px',
                }}
              >
                <div style={{ fontSize: '14px', color: '#2d5a2d' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.summary"
                    values={{
                      volumeCount: isBatchMode ? parseInt(batchCount, 10) : 1,
                      totalItems: totalItemsCount,
                      itemsPerVolume: Math.max(1, parseInt(itemCount, 10) || 1),
                    }}
                    defaultMessage="Will create {volumeCount} volume(s) with {itemsPerVolume} item(s) each (total: {totalItems} items)"
                  />
                </div>
              </div>
            )}
        </div>
      </Modal>

      {/* Advanced Barcode Generation Modal */}
      <BarcodeGenerationModal
        open={showAdvancedBarcodeModal}
        onClose={() => setShowAdvancedBarcodeModal(false)}
        onConfirm={handleAdvancedBarcodeConfirm}
        itemCount={totalItemsCount}
        enableAutoGenerator={enableAutoGenerator}
        autoGenerate={autoGenerate}
        generateBarcodeValue={generateBarcodeValue}
        locationId={locationId}
        locationCode={locationCode}
        existingBarcodes={existingBarcodes}
        title={
          <FormattedMessage
            id="ui-plugin-inventory-templates.volumeManagement.addVolumeModal.advancedBarcodeModal.title"
            defaultMessage="Advanced Barcode Generation Settings"
          />
        }
      />
    </>
  );
};

AddVolumeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  existingVolumes: PropTypes.arrayOf(PropTypes.string),
  fields: PropTypes.object,
  // Props for barcode generation
  enableAutoGenerator: PropTypes.bool,
  autoGenerate: PropTypes.bool,
  generateBarcodeValue: PropTypes.func,
  locationId: PropTypes.string,
  locationCode: PropTypes.string,
  existingBarcodes: PropTypes.arrayOf(PropTypes.string),
};

export default AddVolumeModal;
